// @ts-nocheck
import { formGlobals } from './_/form-globals';
import BaseInput from './_/base-input';

/**
 * Class: SingleCheckboxInput
 * Purpose: Manage one <input type="checkbox"> with required semantics,
 *          Note/Error aria-describedby chaining, and events.
 * Constructor Parameters (types):
 *   wrap (HTMLElement)      - .form_field-wrap
 *   formIndex (number|null)
 *   wrapIndex (number|null)
 *
 * Public Methods:
 *   isValid(): boolean
 *   getState(): { checked:boolean, valid:boolean, blurred:boolean, required:boolean, id:string, value:string }
 *   validateNow(): void
 *   destroy(): void
 *
 * Events:
 *   'validchange' (detail:{ valid:boolean, checked:boolean, instance:SingleCheckboxInput })
 *   'valuechange' (detail:{ checked:boolean, instance:SingleCheckboxInput })
 *   'blur'        (detail:{ instance:SingleCheckboxInput })
 *   'focus'       (detail:{ instance:SingleCheckboxInput })
 * 
 * @class
 * @extends {BaseInput}
 * @this {SingleCheckboxInput}
 */
export default class SingleCheckboxInput extends BaseInput {
  /**
   * @param {HTMLElement} wrap
   * @param {number|null} formIndex
   * @param {number|null} wrapIndex
   */
  constructor(wrap, formIndex = null, wrapIndex = null) {
    super(wrap, formIndex, wrapIndex);

    this.inputId = `checkbox-${formIndex}-${wrapIndex}`;
    /** @type {HTMLInputElement|null} */
    this.input = wrap.querySelector('input[type="checkbox"]');

    // Initialize base class elements
    this._initErrorElement();
    this._initNoteElement(this.wrap.querySelector(formGlobals.noteSelector) || null);

    // config/state
    this.required = this.input?.getAttribute('aria-required') === 'true';
    this.checked = !!this.input?.checked;
    this.valid = this.required ? this.checked : true;
    this.blurred = false;
    this.value = this.input?.value ?? '';

    // listeners
    this._onFocus = this._onFocus.bind(this);
    this._onBlur  = this._onBlur.bind(this);
    this._onChange = this._onChange.bind(this);

    this._setAttributes();
    this._initEvents();
    this._setFilledClass();
  }

  /** @returns {HTMLInputElement|null} */
  _getAriaElement() {
    return this.input;
  }

  _getValidChangeDetail(): { valid: boolean; checked: boolean; instance: SingleCheckboxInput } {
    return {
      valid: this.valid,
      checked: this.checked,
      instance: this
    };
  }

  isValid() { return this.valid; }

  getState() {
    return {
      checked: this.checked,
      valid: this.valid,
      blurred: this.blurred,
      required: this.required,
      id: this.inputId,
      value: this.value
    };
  }

  validateNow() { this._validate(); }

  destroy() {
    super.destroy();
    this.input = null;
  }

  // ---- private
  _setAttributes() {
    if (!this.input) return;

    this.input.setAttribute('id', this.inputId);

    // initial aria-describedby → Note (falls vorhanden)
    this._setAriaDescribedBy({ includeNote: true, includeError: false });
  }

  _initEvents() {
    if (!this.input) return;
    const o = { signal: this._ac.signal };

    this.input.addEventListener('focus', this._onFocus, o);
    this.input.addEventListener('blur',  this._onBlur,  o);
    this.input.addEventListener('change', this._onChange, o);
  }

  _onFocus() {
    this.wrap.classList.add(formGlobals.focusedClass);
    this._dispatch('focus', { instance: this });
  }

  _onBlur() {
    this.wrap.classList.remove(formGlobals.focusedClass);
    const wasFirst = !this.blurred;
    this.blurred = true;
    this._dispatch('blur', { instance: this });
    if (wasFirst && this.required) this._validate();
  }

  _onChange() {
    const prev = this.checked;
    this.checked = !!this.input.checked;
    this.value = this.input.value;

    this._setFilledClass();

    if (prev !== this.checked) {
      this._dispatch('valuechange', { checked: this.checked, instance: this });
    }
    if (this.blurred || this.required) this._validate();
  }

  _setFilledClass() {
    if (this.checked) {
      this.wrap.classList.add(formGlobals.filledClass);
    } else {
      this.wrap.classList.remove(formGlobals.filledClass);
    }
  }

  _validate() {
    const ok = this.required ? this.checked : true;
    if (ok) this._setValid(); else this._setInvalid();
  }

}
